
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Card } from "@shared/ui/card";
import { Info } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";

export const MeterReadingsTab = ({ meterReadings }: { meterReadings: any }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const pageSize = 10;

  const data = meterReadings?.results || [];

  const filterOptions = useMemo(() => {
    const getOptions = (key: string) => {
      if (!data) return [];
      const values = Array.from(new Set(data.map((item: any) => item[key])));
      return values.map((val: any) => ({ label: val, value: val }));
    };

    return {
      month: getOptions("month"),
      cycle_name: getOptions("cycle_name"),
      route_name: getOptions("route_name"),
    };
  }, [data]);

  // Filter Data
  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((item: any) => {
      // Search Filter
      const matchesSearch = Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase())
      );

      if (!matchesSearch) return false;

      // Dropdown Filters
      if (filterValues.month && item.month !== filterValues.month) return false;
      if (
        filterValues.cycle_name &&
        item.cycle_name !== filterValues.cycle_name
      )
        return false;
      if (
        filterValues.route_name &&
        item.route_name !== filterValues.route_name
      )
        return false;

      return true;
    });
  }, [filterValues, searchQuery, data]);

  // Pagination Logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage]);

  const columns: TableColumn<any>[] = [
    {
      key: "month",
      header: "Month",
    },
    {
      key: "cycleName",
      header: "Cycle",
    },
    {
      key: "routeName",
      header: "Route",
    },
    {
      key: "meterNo",
      header: "Meter No",
    },
    {
      key: "deviceNo",
      header: "Device No",
    },
    {
      key: "readingStatus",
      header: "Reading Status",
      render: (value) => {
        return (
          <Badge className={getStatusBadgeClass(value)}>
            {value}
          </Badge>
        );
      },
    },
    {
      key: "previousReading",
      header: "Previous Reading",
      render: (value) => value ?? "-",
    },
    {
      key: "currentReading",
      header: "Current Reading",
      render: (value) => value ?? "-",
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 text-blue-700 px-4 py-3 rounded-md flex items-start gap-2 text-sm border border-blue-100">
        <Info className="h-5 w-5 shrink-0 mt-0.5" />
        <p>
          Only meter readings that have been collected by the meter reader are
          displayed for the selected route.
        </p>
      </div>

      <Card className="border-none shadow-none">
        <GenericTable
          data={paginatedData}
          columns={columns}
          rowKey="meterNo" // Using meter_no as unique key
          pagination={{
            currentPage,
            totalPages: Math.ceil(filteredData.length / pageSize),
            totalItems: filteredData.length,
            itemsPerPage: pageSize,
            startItem: (currentPage - 1) * pageSize + 1,
            endItem: Math.min(currentPage * pageSize, filteredData.length),
          }}
          onPageChange={handlePageChange}
          searchPlaceholder="Search readings..."
          onSearch={(term) => console.log("Searching:", term)}
          emptyMessage="No meter readings found"
        />
      </Card>
    </div>
  );
};
